import * as v from 'valibot'

/**
 * The runtime — the machine seam. Execution lives on its own machine: the
 * control plane decides, the runtime executes, and they meet only through
 * the authenticated calls this vocabulary names. An administrator **enrolls**
 * a runtime (one machine passport, the secret shown once); the runtime then
 * walks one surface only — heartbeat to prove liveness, claim to take
 * dispatched work, report to answer for it. Staff **dispatch** a task onto a
 * job's queue; the queue heals itself (retry with backoff, stale claims
 * recovered when a runtime goes quiet), and every task keeps its own event
 * log, because the machine's story is the record.
 *
 * The lifecycle, as the SDK speaks it:
 *
 *   enroll     an administrator registers the machine; it receives its token
 *   heartbeat  the runtime proves it is alive; the answer names its open work
 *   claim      the runtime takes the oldest task it can run
 *   report     the runtime answers for a held task — succeeded, or failed
 *              (the queue requeues with backoff until the attempts cap)
 *   recover    a claim whose holder went quiet returns to the pool
 *
 * Task kinds are a closed list, a code invariant for now (as priorities are):
 * a runtime declares which of the known kinds it can run, and the queue
 * never hands it one it did not declare.
 */

export const RUNTIME_TASK_KINDS = ['delivery'] as const
export type RuntimeTaskKind = (typeof RUNTIME_TASK_KINDS)[number]

export const RUNTIME_STATUSES = ['active', 'retired'] as const
export type RuntimeStatus = (typeof RUNTIME_STATUSES)[number]

export const RUNTIME_TASK_STATUSES = ['queued', 'claimed', 'succeeded', 'failed', 'cancelled'] as const
export type RuntimeTaskStatus = (typeof RUNTIME_TASK_STATUSES)[number]

/** A runtime, as the console sees it. The token is never here — only its prefix. */
export interface Runtime {
  id: string
  name: string
  description: string | null
  status: RuntimeStatus
  capabilities: RuntimeTaskKind[]
  tokenPrefix: string
  enrolledBy: string
  enrolledByName: string | null
  enrolledAt: string
  lastHeartbeatAt: string | null
  retiredAt: string | null
}

/** The one answer that ever carries the secret — the moment of enrollment. */
export interface RuntimeCreated extends Runtime {
  token: string
}

/** A dispatched task: work the queue holds for the runtimes to claim. */
export interface RuntimeTask {
  id: string
  jobId: string
  jobTitle: string
  runtimeId: string | null
  runtimeName: string | null
  kind: RuntimeTaskKind
  payload: Record<string, unknown>
  status: RuntimeTaskStatus
  attempts: number
  nextAttemptAt: string | null
  lastError: string | null
  claimedAt: string | null
  finishedAt: string | null
  createdBy: string
  createdByName: string | null
  createdAt: string
  updatedAt: string
}

/** One line in a task's own history — the machine's event log. */
export interface RuntimeTaskEvent {
  id: string
  taskId: string
  event: string
  detail: Record<string, unknown> | null
  at: string
}

/** A task with its event log, as the job's dispatch list answers it. */
export type RuntimeTaskDetail = RuntimeTask & { events: RuntimeTaskEvent[] }

/** What a heartbeat answers: liveness stamped, the work still open, what waits. */
export interface RuntimeHeartbeat {
  runtime: Runtime
  /** The tasks this runtime holds and has not finished — its boot-recovery list. */
  open: RuntimeTask[]
  /** How many queued tasks this runtime could claim right now. */
  queued: number
}

const required = (max: number) => v.pipe(v.string(), v.trim(), v.minLength(1, 'This field is required.'), v.maxLength(max, `Use at most ${max} characters.`))
/** Clearable text: a string to set it, null to clear it, absent to leave it. */
const nullableText = (max: number) => v.union([v.pipe(v.string(), v.trim(), v.maxLength(max)), v.null()])

const capabilities = v.pipe(
  v.array(v.picklist(RUNTIME_TASK_KINDS)),
  v.minLength(1, 'Name at least one kind the runtime can run.'),
  v.maxLength(RUNTIME_TASK_KINDS.length),
)

export const createRuntimeSchema = v.pipe(
  v.object({
    name: required(120),
    description: v.optional(nullableText(2000)),
    capabilities,
  }),
  v.check((input) => new Set(input.capabilities).size === input.capabilities.length, 'The capabilities repeat.'),
)

export type CreateRuntimeInput = v.InferOutput<typeof createRuntimeSchema>

/** Absent means unchanged, null means cleared, at least one change required. */
export const updateRuntimeSchema = v.pipe(
  v.object({
    name: v.optional(required(120)),
    description: v.optional(nullableText(2000)),
    capabilities: v.optional(capabilities),
  }),
  v.check((input) => Object.values(input).some((value) => value !== undefined), 'Nothing to change.'),
)

export type UpdateRuntimeInput = v.InferOutput<typeof updateRuntimeSchema>

/** Dispatching: what the task is, what it carries, and — optionally — which machine it waits for. */
export const dispatchTaskSchema = v.object({
  kind: v.picklist(RUNTIME_TASK_KINDS),
  payload: v.optional(v.record(v.string(), v.unknown())),
  runtimeId: v.optional(v.union([required(64), v.null()])),
})

export type DispatchTaskInput = v.InferOutput<typeof dispatchTaskSchema>

/** What a runtime may claim, narrowed to a subset of its declared kinds. */
export const claimTasksSchema = v.object({
  kinds: v.optional(v.pipe(v.array(v.picklist(RUNTIME_TASK_KINDS)), v.maxLength(RUNTIME_TASK_KINDS.length))),
})

export type ClaimTasksInput = v.InferOutput<typeof claimTasksSchema>

/** The answer for a held task: it worked, or it failed with its words. */
export const reportTaskSchema = v.object({
  status: v.picklist(['succeeded', 'failed']),
  error: v.optional(nullableText(1000)),
})

export type ReportTaskInput = v.InferOutput<typeof reportTaskSchema>
